import React, { RefObject } from 'react';
import { LinkIcon, Handshake, Users, CheckCircle } from 'lucide-react';
import { IMAGE_URLS } from '../../constants/images';

interface AboutUsSectionProps {
  visibleSections: Set<string>;
  refs: {
    aboutUsSectionRef: RefObject<HTMLElement>;
    valuesSectionRef: RefObject<HTMLDivElement>;
    relationshipRef: RefObject<HTMLDivElement>;
    respectRef: RefObject<HTMLDivElement>;
    trustworthinessRef: RefObject<HTMLDivElement>;
    valuePeopleRef: RefObject<HTMLDivElement>;
  };
}

const AboutUsSection: React.FC<AboutUsSectionProps> = ({ visibleSections, refs }) => {
  const imageUrl = IMAGE_URLS.ABOUT_US_EVENT_CELEBRATION;
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  // Preload image when component mounts (only once)
  React.useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      setImageLoaded(true);
    };
    img.onerror = () => {
      console.error('Failed to preload image:', imageUrl);
    };
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set image src and handle loading
  React.useEffect(() => {
    if (imgRef.current) {
      const img = imgRef.current;
      // Always set src
      if (img.src !== imageUrl) {
        img.src = imageUrl;
      }
      // Check if image is already loaded (from preload or cache)
      if (img.complete && img.naturalHeight !== 0) {
        setImageLoaded(true);
      }
    }
  }, [imageUrl]);

  return (
    <section
      id="about-us"
      ref={refs.aboutUsSectionRef}
      className="relative z-10 bg-black"
    >
      {/* Gradient overlay for visual depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none z-0" />

      {/* Our Story Part */}
      <div className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2
            className={`font-megant text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 text-center transition-all duration-1000 ${
              visibleSections.has('about-us')
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            OUR STORY
          </h2>
          <div
            className={`font-montserrat space-y-4 sm:space-y-6 text-gray-300 text-base sm:text-lg leading-relaxed transition-all duration-1000 delay-200 ${
              visibleSections.has('about-us')
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            <p className="font-montserrat">
              Everyone suffers the same struggle when planning an event, getting lost between the
              design, attire, guest lists and so on. Yet, most don't even think of invites and
              attendees and leave that to the last minute. As it turns out, that's one of most
              difficult obstacles to tackle. That's where MIRSAL comes in.
            </p>
            <p className="font-montserrat">
              We focus on covering all aspects of customized invite management, including compiling
              and organizing guest lists, collecting all relevant contact details, circulating event
              invitations, gathering and reporting RSVPs, creating a personalized event website,
              offering a dedicated phone line and more. We aim to realize only the most successful
              events possible, all whilst maintaining the highest quality of delivery for clients and
              their guests.
            </p>
          </div>
        </div>
      </div>

      {/* Section Divider with Animation */}
      <div className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 ${
              visibleSections.has('about-us')
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>

      {/* Image with Animation Part - with Parallax */}
      <div className="relative py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div
            className="relative w-full overflow-hidden overflow-x-clip box-border"
            style={{ contain: 'layout style paint' }}
          >
          {/* Image Container - slides from left */}
          <div
            className={`relative w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] transition-transform duration-1000 ease-out z-[1] ${
              visibleSections.has('about-us')
                ? 'translate-x-0 opacity-100'
                : '-translate-x-full opacity-0'
            }`}
          >
            {/* Image with blur-up effect */}
            <div 
              className="relative w-full h-full overflow-hidden"
            >
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gray-900 animate-pulse z-0" />
              )}
              <img
                ref={imgRef}
                src={imageUrl}
                alt="Event celebration"
                className={`w-full h-full object-cover transition-all duration-700 relative z-[1] block ${
                  imageLoaded ? 'opacity-100 blur-0 scale-100' : 'opacity-50 blur-md scale-110'
                }`}
                style={{
                  filter: imageLoaded ? 'none' : 'blur(20px)',
                }}
                onLoad={() => {
                  setImageLoaded(true);
                }}
                onError={(e) => {
                  console.error('Image failed to load:', imageUrl, e);
                  // If image fails to load, still show the container
                  setImageLoaded(true);
                }}
              />
              {/* Gradient overlay on image */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/40 z-10 pointer-events-none" />
            </div>
          </div>

          {/* White Box Overlay - responsive positioning */}
          <div
            className={`absolute top-auto bottom-4 sm:bottom-6 md:top-1/2 md:bottom-auto md:transform md:-translate-y-1/2 right-0 bg-white p-4 sm:p-5 md:p-6 lg:p-8 w-full sm:w-[85%] md:w-[32%] lg:w-[35%] transition-all duration-1000 ease-out z-20 will-change-transform max-w-full box-border ${
              visibleSections.has('about-us')
                ? 'translate-x-0 opacity-100'
                : 'translate-x-full opacity-0'
            }`}
            style={{
              marginRight: visibleSections.has('about-us') ? 'clamp(1rem, 3%, 4%)' : '0',
              visibility: visibleSections.has('about-us') ? 'visible' : 'hidden',
              clipPath: visibleSections.has('about-us') ? 'none' : 'inset(0 100% 0 0)',
              boxShadow: visibleSections.has('about-us')
                ? '0 10px 40px rgba(51, 255, 0, 0.1), 0 0 20px rgba(51, 255, 0, 0.05)'
                : 'none',
            }}
          >
            <h3
              className={`font-megant text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-black mb-2 sm:mb-3 md:mb-4 transition-all duration-700 delay-300 ${
                visibleSections.has('about-us')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-5'
              }`}
            >
              MAKE IT MEMORABLE
            </h3>
            <p
              className={`font-montserrat text-gray-700 text-xs sm:text-sm md:text-base leading-relaxed transition-all duration-700 delay-500 ${
                visibleSections.has('about-us')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-5'
              }`}
            >
              We aspire to set a new standard for guest-host relationships, eliminating the need for
              direct contact between the host & guest.
            </p>
          </div>
          </div>
        </div>
      </div>

      {/* Our Values Part */}
      <div
        id="values"
        ref={refs.valuesSectionRef}
        className="relative z-10 py-20 px-4 sm:px-6 lg:px-8 bg-black"
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/98 to-black pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2
              className={`font-megant text-3xl sm:text-4xl font-bold mb-4 transition-all duration-1000 ${
                visibleSections.has('values')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              OUR VALUES
            </h2>
          </div>

          <div className="space-y-8 sm:space-y-12 md:space-y-16">
            {/* Relationships - slides from left */}
            <div
              ref={refs.relationshipRef}
              data-section-id="relationship"
              className={`flex justify-start transition-all duration-1000 ease-out ${
                visibleSections.has('relationship')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('relationship') ? 'visible' : 'hidden',
              }}
            >
              <div className="text-center max-w-md mx-auto sm:mx-0 sm:ml-8 md:ml-16 lg:ml-24 px-4 sm:px-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
                  <div className="relative">
                    <LinkIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white absolute top-0 left-0" />
                    <LinkIcon className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 absolute top-1 left-1" />
                  </div>
                </div>
                <h3 className="font-megant text-xl sm:text-2xl font-bold text-white mb-3">Relationships</h3>
                <p className="font-montserrat text-gray-300 text-sm sm:text-base leading-relaxed">
                  We forge and maintain close ties with our clients, making sure we meet their
                  every wish and need.
                </p>
              </div>
            </div>

            {/* Respect - slides from right */}
            <div
              ref={refs.respectRef}
              data-section-id="respect"
              className={`flex justify-end transition-all duration-1000 ease-out ${
                visibleSections.has('respect')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('respect') ? 'visible' : 'hidden',
              }}
            >
              <div className="text-center max-w-md mx-auto sm:mx-0 sm:mr-8 md:mr-16 lg:mr-24 px-4 sm:px-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
                  <div className="relative">
                    <Handshake className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-4 h-1.5 sm:w-6 sm:h-2 bg-green-400 rounded-sm"></div>
                  </div>
                </div>
                <h3 className="font-megant text-xl sm:text-2xl font-bold text-white mb-3">Respect</h3>
                <p className="font-montserrat text-gray-300 text-sm sm:text-base leading-relaxed">
                  We uphold the utmost respect for our clients' time.
                </p>
              </div>
            </div>

            {/* Trustworthiness - slides from left */}
            <div
              ref={refs.trustworthinessRef}
              data-section-id="trustworthiness"
              className={`flex justify-start transition-all duration-1000 ease-out ${
                visibleSections.has('trustworthiness')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('trustworthiness') ? 'visible' : 'hidden',
              }}
            >
              <div className="text-center max-w-md mx-auto sm:mx-0 sm:ml-8 md:ml-16 lg:ml-24 px-4 sm:px-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
                  <div className="relative">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-white rounded-full"></div>
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 absolute top-1 left-1" />
                  </div>
                </div>
                <h3 className="font-megant text-xl sm:text-2xl font-bold text-white mb-3">Trustworthiness</h3>
                <p className="font-montserrat text-gray-300 text-sm sm:text-base leading-relaxed">
                  We practice integrity and honesty with each project, ensuring our accountability
                  and reliability.
                </p>
              </div>
            </div>

            {/* Value People - slides from right */}
            <div
              ref={refs.valuePeopleRef}
              data-section-id="valuePeople"
              className={`flex justify-end transition-all duration-1000 ease-out ${
                visibleSections.has('valuePeople')
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-20'
              }`}
              style={{
                visibility: visibleSections.has('valuePeople') ? 'visible' : 'hidden',
              }}
            >
              <div className="text-center max-w-md mx-auto sm:mx-0 sm:mr-8 md:mr-16 lg:mr-24 px-4 sm:px-0">
                <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
                  <div className="relative">
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 rounded-full"></div>
                  </div>
                </div>
                <h3 className="font-megant text-xl sm:text-2xl font-bold text-white mb-3">Value People</h3>
                <p className="font-montserrat text-gray-300 text-sm sm:text-base leading-relaxed">
                  We make sure that our hosts and their guests feel continuously appreciated and
                  cared for.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;

